import { Modal } from 'react-bootstrap';
import { useState, useEffect, useMemo } from "react";
import { validEmail, validUKPhoneNumber, validUKPostCode, notNullOrEmpty, validateField, validTown } from '../../utils/validators';

import './../../pages/properties/property.css';

// New Company Modal Component
export const AddCompanyModal = ({ isOpen, onClose, onSave, api }) => {
    // Company details
    const [companyName, setCompanyName] = useState('');
    const [companyAddress, setCompanyAddress] = useState('');
    const [companyTown, setCompanyTown] = useState('');
    const [companyPostcode, setCompanyPostcode] = useState('');
    const [companyPhone, setCompanyPhone] = useState('');

    // Administrator details
    const [adminFirstName, setAdminFirstName] = useState('');
    const [adminLastName, setAdminLastName] = useState('');
    const [adminUsername, setAdminUsername] = useState('');
    const [adminPhone, setAdminPhone] = useState('');

    // Track which fields have been touched
    const [touchedFields, setTouchedFields] = useState({});

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    // Check if the form is valid
    const isFormValid = useMemo(() => {
        // Check if all required fields have values
        const allFieldsFilled =
            companyName &&
            companyAddress &&
            companyTown &&
            companyPostcode &&
            companyPhone &&
            adminFirstName &&
            adminLastName &&
            adminUsername &&
            adminPhone;

        // Check if there are no validation errors
        const noValidationErrors = Object.keys(validationErrors).length === 0;

        return allFieldsFilled && noValidationErrors;
    }, [
        companyName,
        companyAddress,
        companyTown,
        companyPostcode,
        companyPhone,
        adminFirstName,
        adminLastName,
        adminUsername,
        adminPhone,
        validationErrors
    ]);

    // Helper function to mark a field as touched
    const markFieldAsTouched = (fieldName) => {
        setTouchedFields(prev => ({
            ...prev,
            [fieldName]: true
        }));
    };

    // Helper function to handle field changes
    const handleFieldChange = (fieldName, value, validationFn) => {
        // If the field is empty, remove any validation errors
        if (!value) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
        // Otherwise, validate it (regardless of whether it's been touched)
        else {
            validateField(() => validationFn(value), fieldName, setValidationErrors);
        }

        // Mark the field as touched when it changes
        if (!touchedFields[fieldName]) {
            markFieldAsTouched(fieldName);
        }
    };

    // Company name field
    const handleCompanyNameChange = (e) => {
        const value = e.target.value;
        setCompanyName(value);
        handleFieldChange('companyName', value, notNullOrEmpty);
    };

    // Company address field
    const handleCompanyAddressChange = (e) => {
        const value = e.target.value;
        setCompanyAddress(value);
        handleFieldChange('companyAddress', value, notNullOrEmpty);
    };

    // Company town field
    const handleCompanyTownChange = (e) => {
        const value = e.target.value;
        setCompanyTown(value);
        handleFieldChange('companyTown', value, validTown);
    };

    // Company postcode field
    const handleCompanyPostcodeChange = (e) => {
        const value = e.target.value;
        setCompanyPostcode(value);
        handleFieldChange('companyPostcode', value, validUKPostCode);
    };

    // Company phone field
    const handleCompanyPhoneChange = (e) => {
        const value = e.target.value;
        setCompanyPhone(value);
        handleFieldChange('companyPhone', value, validUKPhoneNumber);
    };

    // Admin first name field
    const handleAdminFirstNameChange = (e) => {
        const value = e.target.value;
        setAdminFirstName(value);
        handleFieldChange('adminFirstName', value, notNullOrEmpty);
    };

    // Admin last name field
    const handleAdminLastNameChange = (e) => {
        const value = e.target.value;
        setAdminLastName(value);
        handleFieldChange('adminLastName', value, notNullOrEmpty);
    };

    // Admin username field (must be in email format)
    const handleAdminUsernameChange = (e) => {
        const value = e.target.value;
        setAdminUsername(value);
        handleFieldChange('adminUsername', value, validEmail);
    };

    // Admin phone field
    const handleAdminPhoneChange = (e) => {
        const value = e.target.value;
        setAdminPhone(value);
        handleFieldChange('adminPhone', value, validUKPhoneNumber);
    };

    const handleSubmit = async e => {
        e.preventDefault();

        // Mark all fields as touched
        setTouchedFields({
            companyName: true,
            companyAddress: true,
            companyTown: true,
            companyPostcode: true,
            companyPhone: true,
            adminFirstName: true,
            adminLastName: true,
            adminUsername: true,
            adminPhone: true
        });

        // Validate all fields
        const newErrors = {};

        if (!notNullOrEmpty(companyName)) newErrors.companyName = false;
        if (!notNullOrEmpty(companyAddress)) newErrors.companyAddress = false;
        if (!validTown(companyTown)) newErrors.companyTown = false;
        if (!validUKPostCode(companyPostcode)) newErrors.companyPostcode = false;
        if (!validUKPhoneNumber(companyPhone)) newErrors.companyPhone = false;
        if (!notNullOrEmpty(adminFirstName)) newErrors.adminFirstName = false;
        if (!notNullOrEmpty(adminLastName)) newErrors.adminLastName = false;
        if (!validEmail(adminUsername)) newErrors.adminUsername = false;
        if (!validUKPhoneNumber(adminPhone)) newErrors.adminPhone = false;

        setValidationErrors(newErrors);

        // Check if there are any validation errors
        if (Object.keys(newErrors).length > 0) {
            setError('Please correct the validation errors before submitting.');
            return;
        }

        submitForm();
    };

    const submitForm = async () => {
        setIsSubmitting(true);
        setError(null);

        try {
            // Create company object from form data
            const newCompany = {
                name: companyName,
                address: companyAddress,
                town: companyTown,
                postcode: companyPostcode,
                phone: companyPhone,
                administrator: {
                    firstName: adminFirstName,
                    lastName: adminLastName,
                    username: adminUsername,
                    email: adminUsername, // Use username as email
                    phone: adminPhone
                }
            };

            // Call API to save the company
            await api.createCompany(newCompany);

            // Reset form and close modal
            resetForm();
            onSave();
        } catch (err) {
            setError('Failed to create company. Please try again.');
            console.error('Error creating company:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        // Reset company fields
        setCompanyName('');
        setCompanyAddress('');
        setCompanyTown('');
        setCompanyPostcode('');
        setCompanyPhone('');

        // Reset administrator fields
        setAdminFirstName('');
        setAdminLastName('');
        setAdminUsername('');
        setAdminPhone('');

        // Reset validation state
        setError(null);
        setValidationErrors({});
        setTouchedFields({});
    };

    // Close and reset form
    const handleClose = () => {
        resetForm();
        onClose();
    };

    // Helper function to determine if a field has an error
    const hasError = (fieldName) => {
        return touchedFields[fieldName] && validationErrors && Object.hasOwn(validationErrors, fieldName);
    };

    // Helper function to get the appropriate CSS class for form fields
    const getInputClass = (fieldName) => {
        return `form-control ${hasError(fieldName) ? 'is-invalid' : ''}`;
    };

    return (
        <Modal
            show={isOpen}
            onHide={handleClose}
            backdrop="static"
            keyboard={true}
            size="lg"
        >
            <Modal.Header>
                <Modal.Title>Add New Company</Modal.Title>
                <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                <form onSubmit={handleSubmit}>
                    <h5 className="mb-3">Company Details</h5>
                    <div className="mb-3">
                        <label htmlFor="companyName" className="form-label">Company Name *</label>
                        <input
                            type="text"
                            className={getInputClass('companyName')}
                            id="companyName"
                            value={companyName}
                            onChange={handleCompanyNameChange}
                            onBlur={() => markFieldAsTouched('companyName')}
                            required
                        />
                        {hasError('companyName') && (
                            <div className="invalid-feedback">
                                Company name is required.
                            </div>
                        )}
                    </div>
                    <div className="mb-3">
                        <label htmlFor="companyAddress" className="form-label">Address *</label>
                        <textarea
                            className={getInputClass('companyAddress')}
                            id="companyAddress"
                            value={companyAddress}
                            onChange={handleCompanyAddressChange}
                            onBlur={() => markFieldAsTouched('companyAddress')}
                            rows="3"
                            required
                        ></textarea>
                        {hasError('companyAddress') && (
                            <div className="invalid-feedback">
                                Address is required.
                            </div>
                        )}
                    </div>
                    <div className="mb-3">
                        <label htmlFor="companyTown" className="form-label">Town/City *</label>
                        <input
                            type="text"
                            className={getInputClass('companyTown')}
                            id="companyTown"
                            value={companyTown}
                            onChange={handleCompanyTownChange}
                            onBlur={() => markFieldAsTouched('companyTown')}
                            placeholder="e.g., London"
                            required
                        />
                        {hasError('companyTown') && (
                            <div className="invalid-feedback">
                                Please enter a valid town or city name.
                            </div>
                        )}
                    </div>
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label htmlFor="companyPostcode" className="form-label">Postcode *</label>
                            <input
                                type="text"
                                className={getInputClass('companyPostcode')}
                                id="companyPostcode"
                                value={companyPostcode}
                                onChange={handleCompanyPostcodeChange}
                                onBlur={() => markFieldAsTouched('companyPostcode')}
                                placeholder="e.g., SW1A 1AA"
                                required
                            />
                            {hasError('companyPostcode') && (
                                <div className="invalid-feedback">
                                    Please enter a valid UK postcode.
                                </div>
                            )}
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="companyPhone" className="form-label">Phone Number *</label>
                            <input
                                type="tel"
                                className={getInputClass('companyPhone')}
                                id="companyPhone"
                                value={companyPhone}
                                onChange={handleCompanyPhoneChange}
                                onBlur={() => markFieldAsTouched('companyPhone')}
                                placeholder="e.g., 07123 456789 or +44 7123 456789"
                                required
                            />
                            {hasError('companyPhone') && (
                                <div className="invalid-feedback">
                                    Please enter a valid UK phone number (e.g., 07123 456789 or +44 7123 456789).
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <hr className="my-4" />
                    
                    <h5 className="mb-3">Company Administrator</h5>
                    <p className="text-muted mb-3">
                        This person will be the primary administrator for your company and will have permissions to add additional users.
                    </p>
                    
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label htmlFor="adminFirstName" className="form-label">First Name *</label>
                            <input
                                type="text"
                                className={getInputClass('adminFirstName')}
                                id="adminFirstName"
                                value={adminFirstName}
                                onChange={handleAdminFirstNameChange}
                                onBlur={() => markFieldAsTouched('adminFirstName')}
                                required
                            />
                            {hasError('adminFirstName') && (
                                <div className="invalid-feedback">
                                    First name is required.
                                </div>
                            )}
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="adminLastName" className="form-label">Last Name *</label>
                            <input
                                type="text"
                                className={getInputClass('adminLastName')}
                                id="adminLastName"
                                value={adminLastName}
                                onChange={handleAdminLastNameChange}
                                onBlur={() => markFieldAsTouched('adminLastName')}
                                required
                            />
                            {hasError('adminLastName') && (
                                <div className="invalid-feedback">
                                    Last name is required.
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label htmlFor="adminUsername" className="form-label">Email Address *</label>
                            <input
                                type="email"
                                className={getInputClass('adminUsername')}
                                id="adminUsername"
                                value={adminUsername}
                                onChange={handleAdminUsernameChange}
                                onBlur={() => markFieldAsTouched('adminUsername')}
                                placeholder="e.g., username@example.com"
                                required
                            />
                            {hasError('adminUsername') && (
                                <div className="invalid-feedback">
                                    Please enter a valid email address.
                                </div>
                            )}
                            <small className="form-text text-muted">
                                This will be used as both username and email address.
                            </small>
                        </div>
                        <div className="col-md-6 mb-3">
                            <label htmlFor="adminPhone" className="form-label">Phone Number *</label>
                            <input
                                type="tel"
                                className={getInputClass('adminPhone')}
                                id="adminPhone"
                                value={adminPhone}
                                onChange={handleAdminPhoneChange}
                                onBlur={() => markFieldAsTouched('adminPhone')}
                                placeholder="e.g., 07123 456789 or +44 7123 456789"
                                required
                            />
                            {hasError('adminPhone') && (
                                <div className="invalid-feedback">
                                    Please enter a valid UK phone number.
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>Cancel</button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || !isFormValid}
                >
                    {isSubmitting ? 'Saving...' : 'Save Company'}
                </button>
            </Modal.Footer>
        </Modal>
    );
};